import React, { useEffect } from 'react';
import Layout from '../components/layout/Layout';
import HeroSection from '../components/shared/HeroSection';
import SectionTitle from '../components/shared/SectionTitle';
import CTASection from '../components/shared/CTASection';
import { FadeInOnScroll } from '../components/shared/Parallax';
import { ExternalLink, Palette, Sun, Shield, Award, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const TessutiPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const collections = [
    {
      name: "Tempotest Star",
      description: "La collezione più ampia con oltre 300 colori uniti e rigati. Perfetta per ogni esigenza.",
      link: "https://www.tempotest.it/it/collezioni/tempotest-star"
    },
    {
      name: "Tempotest Resinati",
      description: "Tessuti con trattamento idrorepellente per massima protezione dagli agenti atmosferici.",
      link: "https://www.tempotest.it/it/collezioni/tempotest-resinati"
    },
    {
      name: "Tempotest Home",
      description: "Tessuti eleganti per tende da interno e decorazione.",
      link: "https://www.tempotest.it/it/collezioni/tempotest-home"
    },
    {
      name: "Tempotest Soft",
      description: "Tessuti morbidi e confortevoli per imbottiti e rivestimenti outdoor.",
      link: "https://www.tempotest.it/it/collezioni/tempotest-soft"
    }
  ];

  const qualities = [
    {
      icon: Sun,
      title: "Protezione UV 97%",
      description: "I tessuti Tempotest bloccano fino al 97% dei raggi UV nocivi."
    },
    {
      icon: Shield,
      title: "Garanzia 5 Anni",
      description: "Garanzia sul colore e sulla resistenza del tessuto per 5 anni."
    },
    {
      icon: Palette,
      title: "Colori Inalterabili",
      description: "Tinti in massa, i colori non sbiadiscono con il sole e il tempo."
    },
    {
      icon: Award,
      title: "Made in Italy",
      description: "Prodotti interamente in Italia con i più alti standard qualitativi."
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <HeroSection
        title="Tessuti Tempotest Parà"
        subtitle="Scopri tutte le collezioni di tessuti che utilizziamo per le nostre tende. Oltre 1000 colori e fantasie disponibili."
        ctaText="Sfoglia le collezioni"
        ctaLink="#collezioni"
        imageUrl="https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=1920&q=80"
        showScrollIndicator={true}
      />

      {/* Intro Section */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="max-w-4xl mx-auto text-center">
              <span className="inline-block text-sm font-medium tracking-widest uppercase mb-4 text-sky-500">
                Partner Ufficiale
              </span>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-gray-900 leading-tight mb-6">
                Perché solo Tempotest Parà
              </h2>
              <p className="text-lg text-gray-600 font-light leading-relaxed mb-8">
                Da oltre 55 anni utilizziamo esclusivamente tessuti <strong>Tempotest Parà</strong>, 
                il marchio italiano leader mondiale nella produzione di tessuti acrilici per outdoor. 
                Ogni tenda che esce dal nostro laboratorio è realizzata con materiali di prima qualità, 
                garantendo durata, colori brillanti e protezione nel tempo.
              </p>
              <a 
                href="https://www.tempotest.it" 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sky-600 hover:text-sky-700 font-medium"
              >
                Visita il sito ufficiale Tempotest
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* Qualities Grid */}
      <section className="py-20 lg:py-28 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <SectionTitle
              label="Caratteristiche"
              title="Qualità certificata"
              description="I tessuti Tempotest sono riconosciuti in tutto il mondo per le loro prestazioni eccezionali."
            />
          </FadeInOnScroll>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {qualities.map((quality, index) => (
              <FadeInOnScroll key={index} delay={index * 100}>
                <div className="text-center p-8 bg-white rounded-lg hover:shadow-lg transition-shadow duration-300">
                  <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-sky-100 flex items-center justify-center">
                    <quality.icon className="w-8 h-8 text-sky-500" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    {quality.title}
                  </h3>
                  <p className="text-gray-600 font-light text-sm">
                    {quality.description}
                  </p>
                </div>
              </FadeInOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Collections Section */}
      <section id="collezioni" className="py-20 lg:py-28 bg-white">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <SectionTitle
              label="Collezioni"
              title="Sfoglia i tessuti disponibili"
              description="Clicca su una collezione per vedere tutti i colori e le fantasie disponibili sul sito ufficiale Tempotest."
            />
          </FadeInOnScroll>

          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {collections.map((collection, index) => (
              <FadeInOnScroll key={index} delay={index * 100}>
                <a 
                  href={collection.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-8 border-2 border-gray-100 hover:border-sky-500 transition-all duration-300 hover:shadow-xl group"
                >
                  <h3 className="text-xl font-medium text-gray-900 mb-3 group-hover:text-sky-500 transition-colors">
                    {collection.name}
                  </h3>
                  <p className="text-gray-600 font-light mb-4">
                    {collection.description}
                  </p>
                  <span className="inline-flex items-center gap-2 text-sky-500 font-medium">
                    Visualizza collezione
                    <ExternalLink className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </span>
                </a>
              </FadeInOnScroll>
            ))}
          </div>

          {/* Main Tempotest Link */}
          <FadeInOnScroll>
            <div className="text-center">
              <a 
                href="https://www.tempotest.it/it/collezioni"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button className="bg-sky-500 hover:bg-sky-600 text-white px-12 py-6 text-lg rounded-none">
                  <Palette className="w-5 h-5 mr-2" />
                  Sfoglia tutte le collezioni Tempotest
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <p className="mt-4 text-sm text-gray-500">
                Segna il codice del colore che preferisci e comunicacelo nel preventivo
              </p>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* How to Order */}
      <section className="py-20 lg:py-28 bg-gradient-to-br from-sky-50 to-blue-50">
        <div className="container mx-auto px-6 lg:px-12">
          <FadeInOnScroll>
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-6">
                Come ordinare il tuo tessuto preferito
              </h2>
              <div className="grid md:grid-cols-3 gap-8 mb-8">
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-sky-500 text-white flex items-center justify-center text-xl font-bold">
                    1
                  </div>
                  <h4 className="font-medium text-gray-900 mb-2">Sfoglia le collezioni</h4>
                  <p className="text-sm text-gray-600">Naviga il catalogo Tempotest e trova il colore perfetto</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-sky-500 text-white flex items-center justify-center text-xl font-bold">
                    2
                  </div>
                  <h4 className="font-medium text-gray-900 mb-2">Segna il codice</h4>
                  <p className="text-sm text-gray-600">Ogni tessuto ha un codice univoco (es. 354/14)</p>
                </div>
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-sky-500 text-white flex items-center justify-center text-xl font-bold">
                    3
                  </div>
                  <h4 className="font-medium text-gray-900 mb-2">Comunicacelo</h4>
                  <p className="text-sm text-gray-600">Inserisci il codice nel form di ordine o preventivo</p>
                </div>
              </div>
              <Link to="/shop">
                <Button className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-4 rounded-none">
                  Vai allo Shop Online
                </Button>
              </Link>
            </div>
          </FadeInOnScroll>
        </div>
      </section>

      {/* CTA Section */}
      <CTASection
        title="Non sai quale tessuto scegliere?"
        description="Vieni nel nostro showroom a Pisa: abbiamo campionari completi di tutti i tessuti Tempotest."
        variant="dark"
      />
    </Layout>
  );
};

export default TessutiPage;
